import React from 'react';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useCmsContent, useLocalesMgr} from 'coreHooks';
import useBannedProduct from 'coreHooks/useBannedProduct';
import {getConfig} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {Content} from 'brand/app/contentful/components';
import ProductBannedMessage from 'brandComponents/product/ProductBannedMessage';
import {ID_PDP_ADDITIONAL_DUTIES_INFO} from 'brandConstants';

import MultiCurrencyProductPrice from './Subcomponents/MultiCurrencyProductPrice';
import ProductPriceSales from './Subcomponents/ProductPriceSales/ProductPriceSales';
import {IProductPrice, IProductPriceProps} from './IProductPrice';

import styles from './ProductPrice.module.scss';

const ProductPrice = ({
	product,
	priceModel: predefinedPriceModel,
	showBanningMessage = true,
	showSalesInParenthesis = false,
	showPercentage = true,
	source,
	classNames,
	isRequiresLoginForRegisteredBannedA = false,
}: IProductPriceProps) => {
	const {managerRepository} = useGeneralProvider();
	const {app: appConfig} = getConfig();
	const showLocalizedPrices = appConfig.showLocalizedPrices;
	const localesMgr = useLocalesMgr();
	const {isBanned} = useBannedProduct(product);
	const pdpAdditionalDutiesInfoConfig = {
		variables: {
			id: ID_PDP_ADDITIONAL_DUTIES_INFO,
		},
	};

	const {data: PDPAdditionalDutiesInfo} = useCmsContent({
		contentType: 'personalizedBlock',
		config: pdpAdditionalDutiesInfoConfig,
		collectionKey: 'personalizedBlockCollection',
	});

	if (isBanned && !isRequiresLoginForRegisteredBannedA) {
		return showBanningMessage ? <ProductBannedMessage /> : null;
	}

	const currentCountry = localesMgr.getCurrentCountry();
	const currencyConfig = currentCountry.getCurrencyConfig();
	const currencyCode = currentCountry.getCurrencyCode();
	const priceModel = product && !predefinedPriceModel ? product.getPriceModel() : predefinedPriceModel;
	const currentCurrency = priceModel.getSales()?.currency || currencyCode;
	const listPrice = priceModel.getList()?.value;
	const salesPrice = priceModel.getSales()?.value;
	const percentage = 100 - Math.round((salesPrice / listPrice) * 100);
	const listPricesArray: IProductPrice[] = [];
	const salesPricesArray: IProductPrice[] = [];
	const sourceMap = currentCountry.getSourceMapping(source);
	const currencyAreaConfig = currencyConfig.areas && currencyConfig.areas[sourceMap];
	const allConversionRates = managerRepository.getConfigurationsMgr().get(`currencyConversionRates`);
	const currencyConversionRates = allConversionRates ? allConversionRates[currentCurrency] : null;

	const getPriceForCurrency = (price: number, currency: string): IProductPrice => {
		if (currency === currentCurrency) {
			return {
				value: price,
				currency,
			};
		} else {
			return {
				value: price * currencyConversionRates[currency],
				currency,
			};
		}
	};

	if (currencyAreaConfig && currencyConversionRates) {
		listPrice &&
			currencyAreaConfig.standard.forEach((currency) => {
				listPricesArray.push(getPriceForCurrency(listPrice, currency));
			});
		salesPrice &&
			currencyAreaConfig.sale.forEach((currency) => {
				salesPricesArray.push(getPriceForCurrency(salesPrice, currency));
			});
	} else {
		listPrice && listPricesArray.push(getPriceForCurrency(listPrice, currentCurrency));
		salesPrice && salesPricesArray.push(getPriceForCurrency(salesPrice, currentCurrency));
	}

	return (
		<div className={cn('product-price', styles['product-price'], classNames?.productPrice)}>
			<div
				className={cn('product-price-multicurrency', styles['product-price-multicurrency'], {
					'has-discount': listPricesArray.length > 0,
				})}
			>
				<MultiCurrencyProductPrice
					pricesArray={salesPricesArray}
					priceType="sales"
					showLocalizedPrices={showLocalizedPrices}
					currencyConfig={currencyConfig}
				/>
			</div>
			{listPricesArray.length > 0 && (
				<div className="percentage">
					<div className={cn('product-price-multicurrency', styles['product-price-multicurrency'])}>
						<MultiCurrencyProductPrice
							pricesArray={listPricesArray}
							priceType="list"
							showLocalizedPrices={showLocalizedPrices}
							currencyConfig={currencyConfig}
						/>
					</div>
					{showPercentage && <ProductPriceSales percentage={percentage} showSalesInParenthesis={showSalesInParenthesis} />}
				</div>
			)}

			{source === EEventSource.PDP && PDPAdditionalDutiesInfo && (
				<div className={styles['product-price__vat-included']}>
					<Content data={PDPAdditionalDutiesInfo} />
				</div>
			)}
		</div>
	);
};

export default ProductPrice;
